// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { Size } from 'models/propTypes/tile';

import Selectable from 'components/focus/Selectable';
import Image from 'components/common/Image';

import { getLogoUrl } from 'support/imageHelper';

import { getGameDate } from 'support/mediaTileHelper';
import { parseDate } from 'common/support/dateHelper';
import styles from './HighlightsTile.module.scss';
import MediaView from './MediaView';

const messages = {
  versus: {
    id: 'scoreCard.teams.versus',
    description: 'versus',
  },
  dot: {
    id: 'common.dot',
    description: 'dot',
  },
  finalScore: {
    id: 'scoreCard.score.finalScore',
    description: 'final score',
  },
  to: {
    id: 'scoreCard.score.to',
    description: 'to',
  },
};

const HighlightsTile = ({
  className,
  size,
  isNowPlaying,
  focusId,
  onClick,
  onFocusSet,
  ttsText,
  renderMetadata,
  autoSizeSrc,
  game,
  ...rest
}) => {
  const intl = useIntl();

  let gameDate = null;
  let gameDateTts = null;
  if (game) {
    gameDate = getGameDate(intl, parseDate(game.scheduledTime));
    gameDateTts = getGameDate(intl, parseDate(game.scheduledTime), true);
  }

  const gameDetail = [];
  if (game.leagueName) {
    gameDetail.push(game.leagueName);
  }
  if (game.typeDetail) {
    gameDetail.push(game.typeDetail);
  }
  const gameDetailText = gameDetail.join(
    ` ${intl.formatMessage(messages.dot)} `,
  );

  const scoreText = `${intl.formatMessage(messages.finalScore)}, ${
    game.awayTeam.score
  } ${intl.formatMessage(messages.to)} ${game.homeTeam.score}`;

  const awayTeamName = game.awayTeam.nickName
    ? game.awayTeam.nickName
    : game.awayTeam.name;
  const homeTeamName = game.homeTeam.nickName
    ? game.homeTeam.nickName
    : game.homeTeam.name;

  const versus = intl.formatMessage(messages.versus);
  const ttsForScore = [
    `${awayTeamName}, ${versus}, ${homeTeamName}`,
    scoreText,
    gameDateTts,
    game.leagueName,
    game.typeDetail,
  ]
    .filter(item => item)
    .join(', ');

  const tts = `${ttsText}, ${ttsForScore}`;

  return (
    <Selectable
      onSelect={onClick}
      focusId={focusId}
      className={className}
      onFocusSet={onFocusSet}
      data-testid="highlights-tile"
      ttsText={tts}
      render={({ hasFocus }) => (
        <div
          className={classNames(
            'd-flex',
            'flex-column',
            styles[`${size}_w`],
            hasFocus ? styles.focused : styles.unfocused,
          )}
        >
          <button
            type="button"
            onClick={onClick}
            // Safari hides the outline if the style is applied to the internal div
            className={classNames('d-flex', 'flex-column', styles.button)}
          >
            <MediaView
              renderMetadata={renderMetadata}
              size={size}
              isNowPlaying={isNowPlaying}
              hasFocus={hasFocus}
              autoSizeSrc={autoSizeSrc}
              isHighlights
              {...rest}
            />

            <span
              className={classNames(
                'd-flex',
                'w-100',
                'flex-column',
                'd-flex',
                styles.scoreCard,
              )}
            >
              <span
                className={classNames(
                  'w-100',
                  'mt-4',
                  styles[`${size}_teams_line_w`],
                  styles.teams,
                )}
              >
                <span className={classNames('font-weight-bold')}>
                  {awayTeamName}
                </span>
                <span> {versus} </span>
                <span className={classNames('font-weight-bold')}>
                  {homeTeamName}
                </span>
              </span>

              <div
                className={classNames(
                  'w-100',
                  'mt-3',
                  'flex-box',
                  'd-flex',
                  styles.scoreDetail,
                )}
              >
                <Image
                  alt="away team logo"
                  src={getLogoUrl(game.awayTeam.id)}
                  className={classNames(styles.teamLogo)}
                  data-testid="away-team-logo"
                />
                <span className={classNames('font-weight-bold', styles.score)}>
                  {game.awayTeam.score}
                </span>
                <Image
                  alt="league logo"
                  src={getLogoUrl(game.leagueId)}
                  className={classNames(styles.leagueLogo)}
                  data-testid="league-logo"
                />
                <span className={classNames('font-weight-bold', styles.score)}>
                  {game.homeTeam.score}
                </span>
                <Image
                  alt="home team logo"
                  src={getLogoUrl(game.homeTeam.id)}
                  className={classNames(styles.teamLogo)}
                  data-testid="home-team-logo"
                />
              </div>

              <div className={classNames('w-100', 'mt-3', styles.date)}>
                {gameDate}
              </div>
              <div className={classNames('w-100', styles.gameDetail)}>
                {gameDetailText}
              </div>
            </span>
          </button>
        </div>
      )}
    />
  );
};

HighlightsTile.propTypes = {
  src: PropTypes.string.isRequired,
  className: PropTypes.string,
  size: Size.isRequired,
  isNowPlaying: PropTypes.bool,
  focusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
  onFocusSet: PropTypes.func,
  ttsText: PropTypes.string.isRequired,
  renderMetadata: PropTypes.func,
  autoSizeSrc: PropTypes.bool, // Tells the mediatile that it's receiving a configurable image source that it should size
  game: PropTypes.object,
};

HighlightsTile.defaultProps = {
  className: null,
  isNowPlaying: false,
  focusId: null,
  onClick: () => {},
  onFocusSet: () => {},
  renderMetadata: null,
  autoSizeSrc: true,
  game: null,
};

export default HighlightsTile;
